import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Mail, ArrowRight, Copy } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { AUTH } from "@/constants/testIds";

const HERO = "https://images.pexels.com/photos/35592814/pexels-photo-35592814.jpeg";

const DEMO_ACCOUNTS = [
    {
        role: "Administrator",
        title: "Head of Digital & Systems",
        email: "admin@sgsbateman.com",
        password: "SgsBateman@2026",
        accent: "#D4AF37",
    },
    {
        role: "Executive",
        title: "EVP — Projects",
        email: "executive@sgsbateman.com",
        password: "Executive@2026",
        accent: "#3B82F6",
    },
    {
        role: "Engineer",
        title: "Lead Engineering Manager",
        email: "engineer@sgsbateman.com",
        password: "Engineer@2026",
        accent: "#10B981",
    },
];

export default function Login() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("admin@sgsbateman.com");
    const [password, setPassword] = useState("SgsBateman@2026");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await login(email.trim(), password);
            toast.success("Signed in to SGS Bateman EPCM360™");
            nav("/dashboard");
        } catch (e2) {
            const msg = e2?.response?.data?.detail || "Authentication failed";
            setErr(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] bg-[var(--sgsb-bg)]">
            {/* Left: Hero */}
            <div className="relative hidden lg:block overflow-hidden">
                <img
                    src={HERO}
                    alt="Industrial engineering site"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 login-vignette" />
                <div className="relative z-10 h-full flex flex-col justify-between p-12">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-md border border-[color:var(--sgsb-gold)]/50 grid place-items-center">
                            <span className="font-display font-black text-[var(--sgsb-gold)]">SGS</span>
                        </div>
                        <div>
                            <div className="font-display font-black tracking-tight text-white text-lg">
                                SGS BATEMAN
                            </div>
                            <div className="kicker text-[var(--sgsb-gold)]">EPCM360™</div>
                        </div>
                    </div>

                    <div className="max-w-lg">
                        <div className="kicker text-[var(--sgsb-gold)] mb-4">
                            Executive EPCM Platform
                        </div>
                        <h1 className="font-display font-black text-white text-5xl xl:text-6xl leading-[0.98] tracking-tighter">
                            Engineering.<br />
                            Procurement.<br />
                            <span className="text-[var(--sgsb-gold)]">Construction Intelligence.</span>
                        </h1>
                        <p className="mt-6 text-slate-300/90 font-body text-base max-w-md leading-relaxed">
                            A world-class portfolio operating system, purpose-built for
                            SGS Bateman&rsquo;s international EPCM operations across
                            mining, metals, energy and infrastructure.
                        </p>
                    </div>

                    <div className="flex items-center gap-8 text-slate-300/80 font-body text-sm">
                        <div>
                            <div className="font-display font-black text-white text-2xl">R42.6bn</div>
                            <div className="text-xs text-slate-400 tracking-wider uppercase">Portfolio Value</div>
                        </div>
                        <div className="h-8 w-px bg-slate-600/60" />
                        <div>
                            <div className="font-display font-black text-white text-2xl">8</div>
                            <div className="text-xs text-slate-400 tracking-wider uppercase">Active Projects</div>
                        </div>
                        <div className="h-8 w-px bg-slate-600/60" />
                        <div>
                            <div className="font-display font-black text-white text-2xl">14</div>
                            <div className="text-xs text-slate-400 tracking-wider uppercase">Countries Served</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right: Auth form */}
            <div className="relative flex items-center justify-center px-6 py-14 lg:px-14 bg-[var(--sgsb-bg)]">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle at 70% 20%, rgba(59,130,246,0.15), transparent 55%), radial-gradient(circle at 20% 90%, rgba(212,175,55,0.15), transparent 55%)",
                    }}
                />

                <div className="relative w-full max-w-md glass rounded-xl p-8">
                    <div className="lg:hidden flex items-center gap-3 mb-6">
                        <div className="h-10 w-10 rounded-md border border-[color:var(--sgsb-gold)]/50 grid place-items-center">
                            <span className="font-display font-black text-[var(--sgsb-gold)]">SGS</span>
                        </div>
                        <div>
                            <div className="font-display font-black text-white text-sm tracking-tight">SGS BATEMAN</div>
                            <div className="kicker text-[var(--sgsb-gold)] text-[10px]">EPCM360™</div>
                        </div>
                    </div>

                    <div className="kicker text-[var(--sgsb-gold)]">Secure sign-in</div>
                    <h2 className="mt-2 font-display font-black text-white text-3xl tracking-tight">
                        Access the executive workspace
                    </h2>
                    <p className="mt-2 text-slate-400 text-sm font-body">
                        Administrator-controlled authentication. Isolated to the SGS Bateman deployment.
                    </p>

                    <div className="gold-line my-6" />

                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <label className="kicker text-slate-400 text-[10px] mb-2 block">Corporate email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" strokeWidth={1.5} />
                                <input
                                    data-testid={AUTH.emailInput}
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-3 py-3 rounded-md bg-slate-950/60 border border-slate-700/70 text-slate-100 font-body text-sm outline-none transition-colors duration-200 focus:border-[color:var(--sgsb-gold)] focus:ring-2 focus:ring-[color:var(--sgsb-gold)]/30"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="kicker text-slate-400 text-[10px] mb-2 block">Password</label>
                            <div className="relative">
                                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" strokeWidth={1.5} />
                                <input
                                    data-testid={AUTH.passwordInput}
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-3 py-3 rounded-md bg-slate-950/60 border border-slate-700/70 text-slate-100 font-body text-sm outline-none transition-colors duration-200 focus:border-[color:var(--sgsb-gold)] focus:ring-2 focus:ring-[color:var(--sgsb-gold)]/30"
                                />
                            </div>
                        </div>

                        {err && (
                            <div
                                data-testid={AUTH.error}
                                className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 font-body"
                            >
                                {err}
                            </div>
                        )}

                        <button
                            data-testid={AUTH.submit}
                            disabled={busy}
                            type="submit"
                            className="group w-full inline-flex items-center justify-center gap-2 py-3 rounded-md bg-[var(--sgsb-gold)] text-slate-950 font-display font-bold tracking-wide text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            {busy ? "Authenticating…" : "Enter workspace"}
                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={2} />
                        </button>
                    </form>

                    <div className="mt-8 flex items-center gap-2 text-xs text-slate-500 font-body">
                        <ShieldCheck className="h-4 w-4 text-[color:var(--sgsb-gold)]" strokeWidth={1.5} />
                        Isolated session. Access logs available to the Head of Digital &amp; Systems.
                    </div>

                    <div className="mt-6 rounded-lg border border-[color:var(--sgsb-gold)]/25 bg-slate-950/60 p-4">
                        <div className="flex items-center justify-between">
                            <div className="kicker text-[var(--sgsb-gold)] text-[10px]">Evaluation Access</div>
                            <span className="text-[10px] text-slate-500 uppercase tracking-widest">tap to fill</span>
                        </div>
                        <div className="mt-3 space-y-2" data-testid="demo-credentials">
                            {DEMO_ACCOUNTS.map((acc) => (
                                <div
                                    key={acc.email}
                                    className="group flex items-center gap-3 rounded-md border border-slate-800 hover:border-[color:var(--sgsb-gold)]/50 bg-slate-900/60 px-3 py-2 transition-colors duration-200"
                                >
                                    <button
                                        type="button"
                                        data-testid={`demo-fill-${acc.role.toLowerCase()}`}
                                        onClick={() => {
                                            setEmail(acc.email);
                                            setPassword(acc.password);
                                            toast.success(`${acc.role} credentials filled`);
                                        }}
                                        className="flex-1 text-left"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="h-1.5 w-1.5 rounded-full"
                                                style={{ background: acc.accent }}
                                            />
                                            <span className="font-display font-bold text-white text-xs tracking-tight">
                                                {acc.role}
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-body truncate">
                                                {acc.title}
                                            </span>
                                        </div>
                                        <div className="mt-1 grid grid-cols-2 gap-2 text-[11px] font-body">
                                            <div className="text-slate-300 truncate">{acc.email}</div>
                                            <div className="text-slate-500 truncate">{acc.password}</div>
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        title="Copy credentials"
                                        data-testid={`demo-copy-${acc.role.toLowerCase()}`}
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(
                                                    `${acc.email} / ${acc.password}`,
                                                );
                                                toast.success(`${acc.role} credentials copied`);
                                            } catch {
                                                toast.error("Clipboard blocked by browser");
                                            }
                                        }}
                                        className="p-1.5 rounded-md text-slate-500 hover:text-[var(--sgsb-gold)] hover:bg-slate-800/70 transition-colors duration-200"
                                    >
                                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 text-[10px] text-slate-500 font-body leading-relaxed">
                            Seeded evaluation accounts. Rotate before production go-live via <span className="text-slate-400">ADMIN_PASSWORD</span> environment variable.
                        </div>
                    </div>

                    <div className="mt-4 text-xs text-slate-600 font-body">
                        Trouble accessing? Contact <span className="text-slate-400">it.access@sgsbateman.com</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
